import React, { useEffect, useState, useMemo } from 'react';
import { createHighlighterCore } from 'shiki/core';
import { createOnigurumaEngine } from 'shiki/engine/oniguruma';

// Import only the specific languages we actually use
import javascript from '@shikijs/langs/javascript';
import typescript from '@shikijs/langs/typescript';
import json from '@shikijs/langs/json';
import css from '@shikijs/langs/css';
import html from '@shikijs/langs/html';
import markdown from '@shikijs/langs/markdown';

// Import only the themes we use
import githubDark from '@shikijs/themes/github-dark';
import githubLight from '@shikijs/themes/github-light';

interface ShikiHighlighterProps {
  children: string;
  language: string;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
  PreTag?: React.ElementType;
}

// Cache for highlighted code to avoid re-processing
const highlightCache = new Map<string, string>();

// Singleton highlighter instance using fine-grained bundle
let highlighterPromise: Promise<any> | null = null;

const createHighlighter = async () => {
  if (!highlighterPromise) {
    highlighterPromise = createHighlighterCore({
      themes: [githubDark, githubLight],
      langs: [javascript, typescript, json, css, html, markdown],
      engine: createOnigurumaEngine(import('shiki/wasm')),
    });
  }
  return highlighterPromise;
};

export const ShikiHighlighter: React.FC<ShikiHighlighterProps> = ({
  children,
  language,
  theme = 'auto',
  className = '',
  PreTag = 'pre',
}) => {
  const [highlightedCode, setHighlightedCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Create a cache key based on content, language, and theme
  const cacheKey = useMemo(() => {
    const themeKey =
      theme === 'auto'
        ? typeof window !== 'undefined' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;
    return `${language}-${themeKey}-${children.substring(0, 100)}`;
  }, [children, language, theme]);

  useEffect(() => {
    const highlightCode = async () => {
      try {
        setIsLoading(true);

        // Check cache first
        if (highlightCache.has(cacheKey)) {
          setHighlightedCode(highlightCache.get(cacheKey)!);
          setIsLoading(false);
          return;
        }

        const highlighter = await createHighlighter();

        // Determine theme based on system preference or explicit theme
        let selectedTheme = 'github-light';
        if (theme === 'dark') {
          selectedTheme = 'github-dark';
        } else if (theme === 'auto') {
          selectedTheme =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'github-dark'
              : 'github-light';
        }

        // Map common language aliases to supported languages
        const languageMap: Record<string, string> = {
          js: 'javascript',
          ts: 'typescript',
          jsx: 'javascript', // Map JSX to JavaScript for simplicity
          tsx: 'typescript', // Map TSX to TypeScript for simplicity
          md: 'markdown',
          htm: 'html',
        };

        const actualLanguage = languageMap[language] || language;

        // Fallback to plain text for unsupported languages
        const supportedLanguages = [
          'javascript',
          'typescript',
          'json',
          'css',
          'html',
          'markdown',
        ];
        const finalLanguage = supportedLanguages.includes(actualLanguage)
          ? actualLanguage
          : 'text';

        const html = await highlighter.codeToHtml(children, {
          lang: finalLanguage,
          theme: selectedTheme,
        });

        // Cache the result
        highlightCache.set(cacheKey, html);
        setHighlightedCode(html);
      } catch (error) {
        console.error('Shiki highlighting error:', error);
        // Fallback to plain text
        setHighlightedCode(
          `<pre class="${className}"><code>${children}</code></pre>`,
        );
      } finally {
        setIsLoading(false);
      }
    };

    highlightCode();
  }, [cacheKey, children, language, theme, className]);

  if (isLoading) {
    return (
      <PreTag className={`${className} shiki-loading`}>
        <code>{children}</code>
      </PreTag>
    );
  }

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: highlightedCode }}
    />
  );
};

// Hook for theme detection
export const useTheme = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isDark ? 'dark' : 'light';
};
